
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import AlarmSetpointEditorUtils, { SetPointAdminMode, SetpointFormType, operators, textOperators, timeComparisonTimeframes, timeOperators, varianceOperators, varianceTimeframes } from "../AlarmSetpointEditorUtils";
import { useFormContext, useWatch } from "react-hook-form";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import FormProperty from "../../forms/form-property/FormProperty";
import FormInput from "../../forms/form-input/FormInput";
import FormSelect from "../../forms/form-select/FormSelect";
import { Alert, MenuItem } from "@mui/material";
import { IReferenceDTO, SetPointPrecedenceTypeEnum } from "../../../services/setpoint-administration-service/Models";
import { DataTypeEnum } from "../../../common/enums/DataTypeEnum";
import { SetPointTypeEnum } from "../../../common/enums/SetpointTypeEnum";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useSetpointReferenceData } from "../../../queries/alarm-setpoint-admin/UseSetpointReferenceData";
import { useGroup } from "../../../queries/groups/UseGroup";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import LoadingZone from "../../loading-zone/LoadingZone";
import { SetPointVarianceTypeEnum } from "../../../common/enums/SetpointVarianceTypeEnum";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { useObjectProperties } from "../../../queries/object-properties/UseObjectProperties";
import { UseQueryResult } from "react-query";
import { ITagProperties } from "../../../services/properties-data-service/Models";
// import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";

export type AlarmSetpointPropertiesProps = {
    groupID: number,
    tagID?: number
};

export default function AlarmSetpointProperties({
    groupID,
    tagID
}: AlarmSetpointPropertiesProps) {

    const { t } = useTranslation();
    const [userState] = useUserContext();
    // const routeContext = useRouteContext();
    const {
        getValues,
        watch,
        trigger,
        control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<SetpointFormType>();    
   
    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });
    
    
    const setpoint = useWatch({ control: control }) as SetpointFormType;

    const organizationID = setpoint.setPointID > 0 ? setpoint.organizationID : group?.organizationID as number;

    const mode = (setpoint?.setPointID ?? 0) <= 0 ? SetPointAdminMode.Add : SetPointAdminMode.Edit;

    const {data: referenceData, status } = useQueryNotifications(useSetpointReferenceData)({
        groupID ,
        organizationID: organizationID,
        enabled: group !== undefined
    });

    const isLoading = status === 'loading';

    const tags = [
        ...setpoint.deviceTags,
        ...setpoint.wellTags,
        ...setpoint.tankTags,
        ...setpoint.tankBatteryTags
    ];

    const multiTagSetpoint = mode === SetPointAdminMode.Edit && tags.length > 1;

    const setpointTagID = tags.length > 0 ? tags[0].tagID : undefined;

    const { data: tagProperties } = useQueryNotifications(useObjectProperties)({        
        groupID: groupID,
        eLynxObject: {
            id: setpointTagID ?? tagID ?? 0, //(routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tag ? routeContext.contextObject?.id : 0),
            objectType: eLynxObjectTypeEnum.Tag
        },
        //enabled: mode === SetPointAdminMode.Edit && tagID ? true : false
        enabled: mode === (SetPointAdminMode.Edit && setpointTagID) || (tagID ?? 0) > 0, //routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tag ? true : false
    }) as UseQueryResult<ITagProperties | undefined>;

    const listText = tagProperties?.lookupLists?.find(o => o.id === tagProperties?.lookupListID)?.listText ?? [];
    const hasLookupList = !multiTagSetpoint && listText?.find(o => o.value === setpoint.value.toString());


    // const distinctThings = thingsWithDuplicates.filter((thing, i, arr) => {
    //     return arr.indexOf(arr.find(t => t.id === thing.id)) === i;
    //   });
    const linkedObjects = tags.filter((item, ndx, arr) => {
        return arr.findIndex(v => v.tagObjectID === item.tagObjectID && v.tagObjectTypeID === item.tagObjectTypeID) === ndx;
    }).length

    const [
        setPointType,
        setPointName, 
        dataType,
        unitClassificationID,
        //unitOfMeasureID,
        comparisonType,
        textValue,
        //varianceType,
        operator,
        isDiscrete,
        // devices,
        // wells,
        // tanks,
        // tankBatteries
        deviceTags,
        wellTags,
        tankTags,
        tankBatteryTags
    ] = watch(['setPointType',
        'setPointName',
        'dataType',
        'unitClassification', 
        //'unitOfMeasureID',
        'comparisonType',
        'textValue',
        //'varianceType',
        'operator',
        'isDiscrete',
        // 'devices',
        // 'wells',
        // 'tanks',
        // 'tankBatteries'
        'deviceTags',
        'wellTags',
        'tankTags',
        'tankBatteryTags'
    ]);

    // const hasSelectedObjects = devices.flatMap(o => o.tags).filter(o => o.selected).length > 0 ||
    //                             wells.flatMap(o => o.tags).filter(o => o.selected).length > 0 ||
    //                             tanks.flatMap(o => o.tags).filter(o => o.selected).length > 0 ||
    //                             tankBatteries.flatMap(o => o.tags).filter(o => o.selected).length > 0 ;

    const hasSelectedObjects = deviceTags.length > 0 ||
                                wellTags.length > 0 ||
                                tankTags.length > 0 ||
                                tankBatteryTags.length > 0 ;

    const uomList = referenceData?.unitsOfMeasure
                    .filter(o => o.unitClassificationID === unitClassificationID || unitClassificationID === -1)
                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())) ?? [];


    const allowVarianceType = comparisonType !== SetPointTypeEnum.ValueComparison && operator !== '<';

    const _validateLessThanZero = (value: string): boolean => {
        return +value > -1;
    }

    function renderValueComparison() {
        return (
            <>
            <FormProperty>
                {dataType === DataTypeEnum.Double ?
                    <FormSelect
                        id="operator"
                        name="operator"
                        label={t('properties.operator')}
                        sx={{ 
                            width: 1,
                            marginTop: 1,
                            }}>

                    {operators.map((o, index) => 
                        <MenuItem key={index} value={o.Value}>
                            {o.Key}
                        </MenuItem>
                    )}

                </FormSelect> :
                <FormSelect
                    id="operator"
                    name="operator"
                    label={t('properties.operator')}
                    sx={{ 
                        width: 1,
                        marginTop: 1,
                        }}>

                    {textOperators.map((o, index) => 
                        <MenuItem key={index} value={o.Value}>
                            {o.Key}
                        </MenuItem>
                    )}
                </FormSelect>
            }

            </FormProperty>  


            {dataType === DataTypeEnum.Double && 
                <>
                <FormProperty>


                    {!hasLookupList ?
                        <FormInput
                            name="value"
                            label={t('properties.value')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                            }}
                            numeric={true}
                            decimals={9}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('setpointAdmin.valueRequired')
                                }, 
                            }}
                        />  :
                        <FormSelect
                                name="value"
                                label={t('properties.value')}
                                sx={{ 
                                    width: 1,
                                    marginTop: 1,
                                    }}>

                                <MenuItem value={-1}>
                                    
                                </MenuItem>

                                {
                                    listText.map((o, index) => 
                                    <MenuItem key={index} value={+(o.value as string)}>
                                        {`${o.value} - ${o.description}`}
                                    </MenuItem>
                                )
                                }

                            </FormSelect>

                        }

                </FormProperty>

                <FormProperty>
                        <FormSelect
                            id="unitClassification"
                            name="unitClassification"
                            label={t('properties.unitClassification')}
                            readOnly={(mode === SetPointAdminMode.Edit && hasSelectedObjects) || 
                                tagID !== undefined
                                //routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag
                            }
                            onChange={() => onUnitClassificationChange()}
                            sx={{ 
                                width: 1,
                                marginTop: 1,
                                }}>

                            <MenuItem value={-1}>
                                Any
                            </MenuItem>

                            {referenceData?.unitClassifications.map((o, index) => 
                                <MenuItem key={index} value={o.id}>
                                    {o.name}
                                </MenuItem>
                            )}

                        </FormSelect>
                    </FormProperty>                

                    <FormProperty>
                        <FormSelect
                            id="unitOfMeasureID"
                            name="unitOfMeasureID"
                            label={t('properties.uom')}
                            readOnly={(mode === SetPointAdminMode.Edit && hasSelectedObjects) || 
                                tagID !== undefined
                                //routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag
                            }
                            sx={{ 
                                width: 1,
                                marginTop: 1,
                                }}>

                            <MenuItem value={-1} disabled={true}>
                                
                            </MenuItem>

                            {uomList.map((o, index) => 
                                <MenuItem key={index} value={o.id}>
                                    {o.name}
                                </MenuItem>
                            )}

                        </FormSelect>
                    </FormProperty>                

                </>
            }

            {dataType === DataTypeEnum.String && 
                <FormProperty>
                    <FormInput
                        name="textValue"
                        label={t('properties.value')}
                        sx={{
                            width: 1,
                            marginTop: 1,
                        }}
                        rules={{
                            required: {
                                value: true,
                                message: t('setpointAdmin.valueRequired')
                            }, 
                            validate: (value: string) => !_validateTextValue(value) ?
                            t('setpointAdmin.textValueLength') : 
                            true
                         }}

                        
                        />

                </FormProperty>
            }
        </>

        )
    }

    function renderVarianceComparison() {
        return (
            <>
            <FormProperty>
                <FormSelect
                    id="varianceType"
                    name="varianceType"
                    label={t('properties.operator')}
                    readOnly={!allowVarianceType}
                    sx={{ 
                        width: 1,
                        marginTop: 1,
                        }}>
                    
                    {allowVarianceType ? varianceOperators.map((o, index) => 
                        <MenuItem key={`vo:${index}`} value={o.Value}>
                            {o.Key}
                        </MenuItem>
                    ) :
                    varianceOperators.filter(o => o.Value === SetPointVarianceTypeEnum.Either).map((o, index) => 
                    <MenuItem key={`vo:${index}`} value={o.Value}>
                        {o.Key}
                    </MenuItem>
                )}

                </FormSelect>
            </FormProperty>  

            <FormProperty>
                <FormInput
                    name="varianceWindow"
                    label={t('setpointAdmin.varianceWindowLabel')}
                    sx={{
                        width: 1,
                        marginTop: 1,
                    }}
                    numeric={true}
                    decimals={0}
                    rules={{
                        required: {
                            value: true,
                            message: t('setpointAdmin.valueRequired')
                        }, 
                    //    validate: (value: string) => !_validateIMEI(value) ?
                    //    t('createDevice.imei15Digits') : 
                    //    true
                    }}
                />

            </FormProperty>


            <FormProperty>
                <FormSelect
                    id="varianceTimeframeType"
                    name="varianceTimeframeType"
                    label={t('properties.timeframe')}
                    sx={{ 
                        width: 1,
                        marginTop: 1,
                        }}>

                    <MenuItem value={-1}>
                    </MenuItem>

                    {varianceTimeframes.map((o, index) => 
                        <MenuItem key={index} value={o.Value}>
                            {o.Key}
                        </MenuItem>
                    )}

                </FormSelect>
            </FormProperty>  

            <FormProperty>
                <FormSelect
                    id="operator"
                    name="operator"
                    //label={t('properties.operator')}
                    label='By'
                    onChange={() => onOperatorChanged()}                    
                    sx={{ 
                        width: 1, 
                        marginTop: 1,
                        }}>

                    {operators.slice(0, 2).map((o, index) => 
                        <MenuItem key={index} value={o.Value}>
                            {o.Value === '>' ? "more than" : o.Key}
                        </MenuItem>
                    )}

                </FormSelect>
            </FormProperty>  

            <FormProperty>
                <FormInput
                    name="value"
                    label={t('properties.value')}
                    sx={{
                        width: 1,
                        marginTop: 1,
                    }}
                    numeric={true}
                    decimals={9}
                    units={comparisonType === SetPointTypeEnum.VariationPercent ? "%" : undefined}
                    rules={{
                        required: {
                            value: true,
                            message: t('setpointAdmin.valueRequired')
                        }, 
                        validate: (value: string) => !_validateLessThanZero(value) ?
                        t('setpointAdmin.valueLessThanZero') : 
                        true
                    }}
                />

            </FormProperty>


            {comparisonType === SetPointTypeEnum.VariationAmount ?
            <>
                <FormProperty>
                    <FormSelect
                        id="unitClassification"
                        name="unitClassification"

                        label={t('properties.unitClassification')}
                        sx={{ 
                            width: 1,
                            marginTop: 1,
                            }}>

                        <MenuItem value={-1}>
                            Any
                        </MenuItem>

                        {referenceData?.unitClassifications.map((o, index) => 
                            <MenuItem key={index} value={o.id}>
                                {o.name}
                            </MenuItem>
                        )}

                    </FormSelect>
                </FormProperty>                

                <FormProperty>
                    <FormSelect
                        id="unitOfMeasureID"
                        name="unitOfMeasureID"
                        label={t('properties.uom')}
                        readOnly={mode === SetPointAdminMode.Edit || 
                            tagID !== undefined
                            // routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag 
                        }
                        sx={{ 
                            width: 1,
                            marginTop: 1,
                            }}>

                        <MenuItem value={0} disabled={true}>
                            
                        </MenuItem>

                        {uomList.map((o, index) => 
                            <MenuItem key={index} value={o.id}>
                                {o.name}
                            </MenuItem>
                        )}

                    </FormSelect>
                </FormProperty>    
                </> :
                null
            }     
        </>

        )
    }

    function renderTimeComparison() {
        return (
            <>
                <FormProperty>
                    <FormSelect
                        id="operator"
                        name="operator"
                        label={t('properties.operator')}
                        readOnly={true}
                        //disabled={true}
                        sx={{
                            width: 1,
                            marginTop: 1,
                        }}>
                        {timeOperators.map((o, index) =>
                            <MenuItem key={index} value={o.Value}>
                                {o.Key}
                            </MenuItem>
                        )}
                    </FormSelect>
                </FormProperty>


                {/* {dataType === DataTypeEnum.Double &&
                    <> */}
                        <FormProperty>
                            <FormInput
                                name="value"
                                label={t('properties.value')}
                                sx={{
                                    width: 1,
                                    marginTop: 1,
                                }}
                                numeric={true}
                                decimals={9}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('setpointAdmin.valueRequired')
                                    },
                                }}
                            /> 
                        </FormProperty>

                        <FormProperty>
                            <FormSelect
                                id="timespanType"
                                name="timespanType"
                                label={t('properties.timeframe')}
                                sx={{ 
                                    width: 1,
                                    marginTop: 1,
                                    }}>

                                <MenuItem value={-1}>
                                </MenuItem>

                                {timeComparisonTimeframes.map((o, index) => 
                                    <MenuItem key={index} value={o.Value}>
                                        {o.Key}
                                    </MenuItem>
                                )}

                            </FormSelect>
                        </FormProperty>


                    {/* </>
                }

                {dataType === DataTypeEnum.String &&
                    <FormProperty>
                        <FormInput
                            name="textValue"
                            label={t('properties.value')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('setpointAdmin.valueRequired')
                                },
                                validate: (value: string) => !_validateTextValue(value) ?
                                    t('setpointAdmin.textValueLength') :
                                    true
                            }}


                        />

                    </FormProperty>
                } */}
            </>

        );
    }

    function onDataTypeChange() {
        const value = getValues();
        setValue('operator', value.dataType === DataTypeEnum.Double ? '<' : 'CONTAINS');

        // setValue('devices', []);
        // setValue('wells', []);
        // setValue('tankBatteries', []);
        // setValue('tanks', []);
        setValue("deviceTags", []);
        setValue("wellTags", []);
        setValue("tankTags", []);
        setValue("tankBatteryTags", []);

        setValue('comparisonType', SetPointTypeEnum.ValueComparison);

        resetAlarmMessages();
    }

    function onUnitClassificationChange() {
        const value = getValues();

        const uoms = referenceData?.unitsOfMeasure.filter(o => o.unitClassificationID === value.unitClassification || value.unitClassification === -1) ?? [];

        if (uoms.length) {
            setValue('unitOfMeasureID', uoms[0].id);
        }

        // setValue('devices', []);
        // setValue('wells', []);
        // setValue('tankBatteries', []);
        // setValue('tanks', []);
        setValue("deviceTags", []);
        setValue("wellTags", []);
        setValue("tankTags", []);
        setValue("tankBatteryTags", []);
    }

    function onSetpointTypeChange() {

        const value = getValues('setPointType');
        if (value === SetPointPrecedenceTypeEnum.Field) {
            setValue('isDiscrete', true);
            setValue('operator', '>');
            setValue('value', 0);
        } else {
            setValue('isDiscrete', false);
        }

        // setValue('devices', []);
        // setValue('wells', []);
        // setValue('tankBatteries', []);
        // setValue('tanks', []);
        setValue("deviceTags", []);
        setValue("wellTags", []);
        setValue("tankTags", []);
        setValue("tankBatteryTags", []);

        setValue('sampleEmailAlarmMessageSubject', '');
        setValue('sampleEmailAlarmMessageBody', '');
        setValue('sampleEmailClearMessageSubject', '');
        setValue('sampleEmailClearMessageBody', '');
        setValue('sampleVoiceAlarmMessageBody', '');
        setValue('sampleVoiceClearMessageBody', '');
    }

    function resetAlarmMessages() {

        const value = getValues();

        const templates = AlarmSetpointEditorUtils.getMessageTemplates(value, referenceData as IReferenceDTO);

        setValue('emailAlarmMessageSubject', templates.emailAlarmMessageSubject);
        setValue('emailAlarmMessageBody', templates.emailAlarmMessageBody);
        setValue('emailClearMessageSubject', templates.emailClearMessageSubject);
        setValue('emailClearMessageBody', templates.emailClearMessageBody);
        setValue('voiceAlarmMessageBody', templates.voiceAlarmMessageBody);
        setValue('voiceClearMessageBody', templates.voiceClearMessageBody);


        setValue('sampleEmailAlarmMessageSubject', '');
        setValue('sampleEmailAlarmMessageBody', '');
        setValue('sampleEmailClearMessageSubject', '');
        setValue('sampleEmailClearMessageBody', '');
        setValue('sampleVoiceAlarmMessageBody', '');
        setValue('sampleVoiceClearMessageBody', '');
    }

    function onComparisonTypeChanged() {


        const value = getValues('comparisonType');

        if(value === SetPointTypeEnum.ValueComparison) {
            setValue('operator', '<');
        } else if (value === SetPointTypeEnum.VariationPercent || value === SetPointTypeEnum.VariationAmount) {
            setValue('operator', '>');
        } else if (value === SetPointTypeEnum.TimeComparison) {
            setValue('operator', 'OLDER THAN');
        }

        resetAlarmMessages();

        trigger();
    }

    function onOperatorChanged() {

        const value = getValues('operator');

        if (comparisonType !== SetPointTypeEnum.ValueComparison && value === '<') {
            setValue('varianceType', SetPointVarianceTypeEnum.Either);
            trigger();
        }
    }


    const _validateSetPointName = (value: string): boolean => {
        return value.toString().trim().length <= 50;
    }

    const _validateTextValue = (value: string): boolean => {
        return value.toString().trim().length <= 100;
    }


    // execute on every render
    useEffect(() => {

        setTimeout(() => {
            const items: ('setPointName' | 'textValue')[] = [];

            if ((setPointName?.trim().length ?? 0) === 0) {
                items.push('setPointName');
            }

            if (dataType === DataTypeEnum.String && (textValue?.trim().length ?? 0) === 0) {
                items.push('textValue')
            }

            if (items.length) {
                trigger(items);
            }

        }, 1);
    // }, [
    //     dataType,
    //     setPointName,
    //     textValue,
    //     trigger
    // ]
    }
    );

   return (

    <LoadingZone isLoading={isLoading}>
        {!isLoading && <ContentContainer>
            <ContentSection sx={{
                padding: 1,
                overflowY: 'auto'
            }}>
            
                <FormPropertySection label={t('properties.standard')}>

                    <FormProperty>
                        <FormInput
                            name="setPointName"
                            label={t('properties.name')}
                            sx={{
                                width: 1,
                                marginTop: 1,
                                marginBottom: 1,
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: t('setpointAdmin.setpointNameRequired')
                                },
                                validate: (value: string) => !_validateSetPointName(value) ?
                                t('setpointAdmin.setpointNameLength') : 
                                true
                            }}

                        />
                    </FormProperty>

                    <FormProperty>
                    <FormSelect
                            id="setPointType"
                            name="setPointType"
                            label={t('properties.type')}
                            readOnly={mode === SetPointAdminMode.Edit && hasSelectedObjects}
                            sx={{ 
                                width: 1,
                                marginTop: 1,
                            }}
                            onChange={() => onSetpointTypeChange()}
                        >
                            <MenuItem value={SetPointPrecedenceTypeEnum.Database}>
                                Database Alarm
                            </MenuItem>
                            <MenuItem value={SetPointPrecedenceTypeEnum.Field}>
                                Field Alarm
                            </MenuItem>
                        </FormSelect>

                    </FormProperty>

                    {
                        !isDiscrete && setPointType === SetPointPrecedenceTypeEnum.Database ? 
                        <>
                            <FormProperty>
                            <FormSelect
                                    id="dataType"
                                    name="dataType"
                                    label={t('properties.dataType')}
                                    readOnly={(mode === SetPointAdminMode.Edit && hasSelectedObjects) || 
                                        tagID !== undefined
                                        // routeContext?.contextObject?.objectType === eLynxObjectTypeEnum.Tag 
                                    }
                                    sx={{ 
                                        width: 1,
                                        marginTop: 1,
                                    }}
                                    onChange={() => onDataTypeChange()}
                                >
                                    <MenuItem value={DataTypeEnum.Double}>
                                        Numeric
                                    </MenuItem>
                                    <MenuItem value={DataTypeEnum.String}>
                                        Text
                                    </MenuItem>
                                </FormSelect>

                            </FormProperty>

                            {dataType === DataTypeEnum.Double && <FormProperty>
                                <FormSelect
                                    id="comparisonType"
                                    name="comparisonType"
                                    label={t('properties.comparison')}
                                    readOnly={mode === SetPointAdminMode.Edit && hasSelectedObjects && 
                                        (comparisonType === SetPointTypeEnum.VariationAmount || comparisonType === SetPointTypeEnum.VariationPercent)}
                                    sx={{ 
                                        width: 1,
                                        marginTop: 1,
                                    }}
                                    onChange={() => onComparisonTypeChanged()}
                                >
                                    <MenuItem value={SetPointTypeEnum.ValueComparison}>
                                        {t('setpointAdmin.valueComparison')}
                                    </MenuItem>
                                    <MenuItem value={SetPointTypeEnum.VariationPercent}>
                                        {t('setpointAdmin.variancePct')}
                                    </MenuItem>
                                    <MenuItem value={SetPointTypeEnum.VariationAmount}>
                                        {t('setpointAdmin.varianceAmt')}
                                    </MenuItem>
                                    <MenuItem value={SetPointTypeEnum.TimeComparison}>
                                        {t('setpointAdmin.timeComparison')}
                                    </MenuItem>
                                </FormSelect>

                            </FormProperty>
                            }

                            {dataType === DataTypeEnum.String && <FormProperty>
                                <FormSelect
                                    id="comparisonType"
                                    name="comparisonType"
                                    label={t('properties.comparison')}
                                    readOnly={mode === SetPointAdminMode.Edit && hasSelectedObjects && 
                                        (comparisonType === SetPointTypeEnum.VariationAmount || comparisonType === SetPointTypeEnum.VariationPercent)}
                                    sx={{ 
                                        width: 1,
                                        marginTop: 1,
                                    }}
                                    onChange={() => onComparisonTypeChanged()}
                                >
                                    <MenuItem value={SetPointTypeEnum.ValueComparison}>
                                        {t('setpointAdmin.valueComparison')}
                                    </MenuItem>
                                    <MenuItem value={SetPointTypeEnum.TimeComparison}>
                                        {t('setpointAdmin.timeComparison')}
                                    </MenuItem>
                                </FormSelect>

                            </FormProperty>
                            }
                        </> : 
                        null
                    }

                </FormPropertySection>

                {
                    setPointType === SetPointPrecedenceTypeEnum.Database && comparisonType === SetPointTypeEnum.ValueComparison && 
                    <FormPropertySection label={t(dataType === DataTypeEnum.String ? 'setpointAdmin.alarmWhen' : 'setpointAdmin.alarmWhenIs')}>
                        {renderValueComparison() }
                    </FormPropertySection>
                }

                {
                    setPointType === SetPointPrecedenceTypeEnum.Database &&
                    (comparisonType === SetPointTypeEnum.VariationAmount ||
                    comparisonType === SetPointTypeEnum.VariationPercent) &&
                    <FormPropertySection label={t('setpointAdmin.alarmWhenIs')}>
                        {renderVarianceComparison()}
                    </FormPropertySection>
                }

                {
                    setPointType === SetPointPrecedenceTypeEnum.Database && comparisonType === SetPointTypeEnum.TimeComparison &&
                    <FormPropertySection label={t('setpointAdmin.alarmWhenTimeIs')}>
                        {renderTimeComparison()}
                    </FormPropertySection>
                }



                {
                    setpoint.setPointType === SetPointPrecedenceTypeEnum.Database && 
                    setpoint.dataType !== DataTypeEnum.String && 
                    //setpoint.comparisonType !== SetPointTypeEnum.VariationPercent &&
                    (setpoint.comparisonType === SetPointTypeEnum.ValueComparison ||
                    setpoint.comparisonType === SetPointTypeEnum.VariationAmount) &&

                    <Alert severity="info"
                        sx={{
                            marginTop: 1,
                        }}>
                        {t('setpointAdmin.uomHint')}
                    </Alert>
                }
                {
                    tags.length > 1 &&
                    <Alert severity="warning"
                        sx={{
                            marginTop: 1,
                        }}>
                        {t('setpointAdmin.tagAttachmentStatement', {
                            tagCount: tags.length,
                            objectCount: linkedObjects
                        })}
                    </Alert>

                }
                {
                    tags.some(tag => tag.tagUomIsIncompatible) &&
                        <Alert severity="error"
                            sx={{
                                marginTop: 1,
                            }}>
                            {t('setpointAdmin.hasIncompatibleTag')}
                        </Alert>
                }
            </ContentSection>        
        </ContentContainer>
        }
    </LoadingZone>
        
    );
  }
